import React, { useState, useEffect, useRef } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { CSSTransition } from 'react-transition-group';
import styled from 'styled-components';
import { srConfig } from '@config';
import { KEY_CODES } from '@utils';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledJobsSection = styled.section`
  max-width: 900px;
  padding-bottom: 0;

  .inner {
    display: grid;
    grid-template-columns: max-content minmax(0, 520px);
    column-gap: 20px;
    align-items: start;
    justify-content: center;

    @media (max-width: 768px) {
      display: block;
    }

    // Prevent container from jumping
    @media (min-width: 700px) {
      min-height: 340px;
    }
  }
`;

const StyledTabList = styled.div`
  position: relative;
  z-index: 3;
  width: max-content;
  padding: 0;
  margin: 0;
  list-style: none;

  @media (max-width: 768px) {
    display: flex;
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
    overflow-x: auto;
    padding-bottom: 4px;
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const StyledTabControls = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
    opacity: 0.88;

    button {
      ${({ theme }) => theme.mixins.flexCenter};
      width: 32px;
      height: var(--tab-height);
      border: 1px solid var(--lightest-navy);
      border-radius: var(--border-radius);
      background-color: transparent;
      color: var(--slate);
      font-family: var(--font-mono);
      font-size: var(--fz-xs);
      transition: var(--transition);

      &:hover,
      &:focus {
        border-color: var(--green);
        color: var(--green);
        background-color: var(--green-tint);
      }

      &:disabled {
        opacity: 0.35;
        cursor: default;
      }

      &:disabled:hover,
      &:disabled:focus {
        border-color: var(--lightest-navy);
        color: var(--slate);
        background-color: transparent;
      }
    }
  }
`;

const StyledTabRail = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: max-content;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const StyledMobileTabStrip = styled.div`
  display: block;

  @media (max-width: 768px) {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0 0 0.35rem;
  }
`;

const StyledTabButton = styled.button`
  ${({ theme }) => theme.mixins.link};
  display: flex;
  align-items: center;
  width: 100%;
  height: var(--tab-height);
  padding: 0 20px 2px;
  border-left: 2px solid var(--lightest-navy);
  border-bottom: 0;
  background-color: transparent;
  color: ${({ isActive }) => (isActive ? 'var(--green)' : 'var(--slate)')};
  font-family: var(--font-mono);
  font-size: var(--fz-xs);
  text-align: left;
  white-space: nowrap;

  .title {
    @media (max-width: 768px) {
      display: inline;
    }
  }

  .mobile_title {
    display: none;
  }

 @media (max-width: 768px) {
  ${({ theme }) => theme.mixins.flexCenter};
  flex: 0 0 auto;
  width: auto;
  min-width: max-content;
  height: var(--tab-height);
  padding: 0 16px 2px;
  border-left: 0;
  border-bottom: 2px solid ${({ isActive }) => (isActive ? 'var(--green)' : 'var(--lightest-navy)')};
  text-align: center;
}

  @media (max-width: 480px) {
    width: auto;
  }

  &:hover,
  &:focus {
    background-color: var(--light-navy);
  }
`;

const StyledHighlight = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
  width: 2px;
  height: var(--tab-height);
  border-radius: var(--border-radius);
  background: var(--green);
  transform: translateY(calc(${({ activeTabId }) => activeTabId} * var(--tab-height)));
  transition: transform 0.25s cubic-bezier(0.645, 0.045, 0.355, 1);
  transition-delay: 0.1s;

  @media (max-width: 768px) {
    top: auto;
    bottom: 0;
    height: 2px;
    width: 0;
    transform: none;
    background: transparent;
  }
`;

const StyledTabPanels = styled.div`
  position: relative;
  width: 100%;
  min-height: 470px;
  margin-top: 1rem;

  @media (max-width: 768px) {
    margin-top: 1.5rem;
    min-height: 700px;
  }
`;

const StyledExperienceSummary = styled.div`
  width: 100%;
  margin-top: 1rem;
  margin-bottom: 1.75rem;
  padding: 1rem 1.1rem;
  border: 1px solid var(--lightest-navy);
  border-left: 2px solid var(--green);
  border-radius: var(--border-radius);
  background: var(--light-navy);
  box-shadow: 0 10px 30px -15px var(--navy-shadow);

  .label {
    margin-bottom: 0.35rem;
    color: var(--green);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .value {
    color: var(--lightest-slate);
    font-size: var(--fz-xl);
    font-weight: 600;
    line-height: 1.1;
  }

  .note {
    margin-top: 0.35rem;
    color: var(--slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xxs);
  }
`;

const MONTHS = {
  January: 0,
  February: 1,
  March: 2,
  April: 3,
  May: 4,
  June: 5,
  July: 6,
  August: 7,
  September: 8,
  October: 9,
  November: 10,
  December: 11,
};

function parseRangeToMonths(range) {
  if (!range || typeof range !== 'string') {
    return 0;
  }

  const [startRaw, endRaw] = range.split(' - ').map(part => part.trim());
  const parseDate = value => {
    if (!value || value === 'Present') {
      return null;
    }

    const [monthName, yearText] = value.split(' ');
    const month = MONTHS[monthName];
    const year = Number(yearText);

    if (month === undefined || Number.isNaN(year)) {
      return null;
    }

    return new Date(year, month, 1);
  };

  const start = parseDate(startRaw);
  const end = endRaw === 'Present' ? new Date() : parseDate(endRaw);

  if (!start || !end) {
    return 0;
  }

  return Math.max(0, (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()));
}

function formatTotalExperience(totalMonths) {
  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;

  if (years && months) {
    return `${years} year${years === 1 ? '' : 's'} ${months} month${months === 1 ? '' : 's'}`;
  }

  if (years) {
    return `${years} year${years === 1 ? '' : 's'}`;
  }

  return `${months} month${months === 1 ? '' : 's'}`;
}

const StyledTabPanel = styled.div`
  width: 100%;
  height: auto;
  padding: 10px 5px;

  ul {
    ${({ theme }) => theme.mixins.fancyList};
  }

  h3 {
    margin-bottom: 2px;
    font-size: var(--fz-xxl);
    font-weight: 500;
    line-height: 1.3;

    .company {
      color: var(--green);
    }
  }

  .range {
    margin-bottom: 25px;
    color: var(--light-slate);
    font-family: var(--font-mono);
    font-size: var(--fz-xs);
  }
`;

const Jobs = () => {
  const data = useStaticQuery(graphql`
    query {
      jobs: allMarkdownRemark(
        filter: { fileAbsolutePath: { regex: "/content/jobs/" } }
        sort: { fields: [frontmatter___date], order: DESC }
      ) {
        edges {
          node {
            frontmatter {
              title
              company
              location
              range
              url
            }
            html
          }
        }
      }
    }
  `);

  const jobsData = data.jobs.edges;

  const [activeTabId, setActiveTabId] = useState(0);
  const [tabFocus, setTabFocus] = useState(null);
  const tabs = useRef([]);
  const tabList = useRef(null);
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const totalExperienceMonths = jobsData.reduce((sum, { node }) => {
    return sum + parseRangeToMonths(node.frontmatter.range);
  }, 0);
  const totalExperience = formatTotalExperience(totalExperienceMonths);

  const moveMobileTab = direction => {
    const nextTab = Math.min(Math.max(activeTabId + direction, 0), jobsData.length - 1);
    setActiveTabId(nextTab);
    tabs.current[nextTab]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    });
  };
  
  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, [prefersReducedMotion]);

  // Only re-run the effect if tabFocus changes
  useEffect(() => {
    const focusTab = () => {
      if (tabs.current[tabFocus]) {
        tabs.current[tabFocus].focus();
        return;
      }
      // If we're at the end, go to the start
      if (tabFocus >= tabs.current.length) {
        setTabFocus(0);
      }
      // If we're at the start, move to the end
      if (tabFocus < 0) {
        setTabFocus(tabs.current.length - 1);
      }
    };

    focusTab()
  }, [tabFocus]);

  // Focus on tabs when using up & down arrow keys
  const onKeyDown = e => {
    switch (e.key) {
      case KEY_CODES.ARROW_UP: {
        e.preventDefault();
        setTabFocus(tabFocus - 1);
        break;
      }

      case KEY_CODES.ARROW_DOWN: {
        e.preventDefault();
        setTabFocus(tabFocus + 1);
        break;
      }

      default: {
        break;
      }
    }
  };

  return (
    <StyledJobsSection id="jobs" ref={revealContainer}>
      <h2 className="numbered-heading">Where I’ve Worked</h2>

      <div className="inner">
        <StyledTabRail>
          <StyledExperienceSummary aria-label="Total experience summary">
            <div className="label">Total Experience</div>
            <div className="value">{totalExperience}</div>
            <div className="note">Across all listed roles</div>
          </StyledExperienceSummary>

          <StyledMobileTabStrip>
            <StyledTabControls>
              <button
                type="button"
                aria-label="Previous job"
                onClick={() => moveMobileTab(-1)}
                disabled={activeTabId === 0}>
                &lt;
              </button>
            </StyledTabControls>
            <StyledTabList
              ref={tabList}
              role="tablist"
              aria-label="Job tabs"
              onKeyDown={e => onKeyDown(e)}>
              {jobsData &&
                jobsData.map(({ node }, i) => {
                  const { company } = node.frontmatter;
                  return (
                    <StyledTabButton
                      key={i}
                      isActive={activeTabId === i}
                      onClick={() => {
                        setActiveTabId(i);
                        tabs.current[i]?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'nearest',
                          inline: 'center',
                        });
                      }}
                      ref={el => (tabs.current[i] = el)}
                      id={`tab-${i}`}
                      role="tab"
                      tabIndex={activeTabId === i ? '0' : '-1'}
                      aria-selected={activeTabId === i ? true : false}
                      aria-controls={`panel-${i}`}>
                      <span className="title">{company}</span>
                    </StyledTabButton>
                  );
                })}
              <StyledHighlight activeTabId={activeTabId} />
            </StyledTabList>
            <StyledTabControls>
              <button
                type="button"
                aria-label="Next job"
                onClick={() => moveMobileTab(1)}
                disabled={activeTabId === jobsData.length - 1}>
                &gt;
              </button>
            </StyledTabControls>
          </StyledMobileTabStrip>
        </StyledTabRail>

        <StyledTabPanels>
          {jobsData &&
            jobsData.map(({ node }, i) => {
              const { frontmatter, html } = node;
              const { title, url, company, range } = frontmatter;

              return (
                <CSSTransition key={i} in={activeTabId === i} timeout={250} classNames="fade">
                  <StyledTabPanel
                    id={`panel-${i}`}
                    role="tabpanel"
                    tabIndex={activeTabId === i ? '0' : '-1'}
                    aria-labelledby={`tab-${i}`}
                    aria-hidden={activeTabId !== i}
                    hidden={activeTabId !== i}>
                    <h3>
                      <span>{title}</span>
                      <span className="company">
                        &nbsp;@&nbsp;
                        <span className="inline-link">
                          {company}
                        </span>
                      </span>
                    </h3>

                    <p className="range">{range}</p>

                    <div dangerouslySetInnerHTML={{ __html: html }} />
                  </StyledTabPanel>
                </CSSTransition>
              );
            })}
        </StyledTabPanels>
      </div>
    </StyledJobsSection>
  );
};

export default Jobs;
